import { capitalize, getRequiredFields } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { schema } from "./validations";
import { toast } from "@/components/Toast";
import {
  useGetBillingCategoryByCommunity,
  useGetDocumentTypeDropdown,
  useGetGlMasterData,
  useGetPaymentTerms,
  useGetPostedDocById,
  useGetPropertyBlocks,
  useGetPropertyModels,
} from "@/services";
import {
  BILLING_AMOUNT,
  BILLING_CATEGORY,
  BILLING_DATE,
  BILLING_MONTH_YEAR,
  CHECK,
  COMMENT,
  CURR_OWNER_ID,
  CURR_OWNER_LAST_NAME,
  DOCUMENT_ID,
  DOCUMENT_TYPE,
  DRCR,
  DUE_DATE,
  GL_ACCOUNT,
  ITEM_TEXT,
  LINE_BILLING_AMOUNT,
  LINE_ITEMS,
  PAYMENT_TERM,
  POSTING_DATE,
  PROPERTY_BLOCK,
  PROPERTY_ID,
  PROPERTY_MODEL,
  PROPERTY_NAME,
  STREET_ADDRESS,
  TRANSACTION_TYPE,
  UNIT_NO,
} from "./consts";
import moment from "moment";

const useAddEdit = () => {
  const { billingId, action } = useParams();
  const fromDashboard = useLocation().pathname?.includes("/dashboard");
  const navigate = useNavigate();
  const [keys, setKeys] = useState({
    accountName: Math.random() * 2,
    documentTypes: Math.random() * 2,
    paymentTerm: Math.random() * 2,
    billingCategory: Math.random() * 2,
    propertyModel: Math.random() * 2,
    transactionType: Math.random() * 2,
  });
  const [filterCriteria, setFilterCriteria] = useState({
    [PROPERTY_NAME]: "",
    [CURR_OWNER_LAST_NAME]: "",
  });
  const [isEntryView, setIsEntryView] = useState<boolean>(true);

  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );

  const form = useForm<any>({
    defaultValues: {
      [DOCUMENT_TYPE]: "",
      [DOCUMENT_ID]: "",
      [BILLING_MONTH_YEAR]: "",
      [BILLING_DATE]: "",
      [BILLING_AMOUNT]: "",
      [DUE_DATE]: "",
      [TRANSACTION_TYPE]: "",
      [GL_ACCOUNT]: "",
      [PAYMENT_TERM]: "",
      [COMMENT]: "",
      [POSTING_DATE]: "",
      // [POSTING_MONTH]: "",
      // [YEAR]: "",
      [LINE_ITEMS]: [
        {
          [BILLING_CATEGORY]: "",
          [PROPERTY_MODEL]: "",
          [PROPERTY_BLOCK]: "",
          [PROPERTY_ID]: "",
          [PROPERTY_NAME]: "",
          [UNIT_NO]: "",
          [STREET_ADDRESS]: "",
          [CURR_OWNER_ID]: "",
          [CURR_OWNER_LAST_NAME]: "",
          [DRCR]: "",
          [LINE_BILLING_AMOUNT]: "",
          [ITEM_TEXT]: "",
          [CHECK]: false,
        },
      ],
    },
    resolver: zodResolver(schema),
  });

  const requiredFields = getRequiredFields(schema);
  
  const breadcrumbDataActivity = useMemo(() => {
    return [
      {
        link: `/${routes.cam}/${routes.activityDashboard}`,
        name: `Activity Dashboard - ${communityDetail?.name || ""}`,
      },
      {
        link: `/${routes.cam}/${routes.activityDashboard}/${routes.billedForPeriod}`,
        name: <span className="text-primary">{"Billed for the Period"}</span>,
      },
      {
        link: "",
        name: <span className="text-primary">{capitalize(action || "")}</span>,
      },
    ];
  }, []);

  const breadcrumbDataDashboard = useMemo(() => {
    return [
      {
        link: `/${routes.cam}/${routes.dashboard}`,
        name: `Dashboard - ${communityDetail?.name || ""}`,
      },
      {
        link: `/${routes.cam}/${routes.dashboard}/${routes.billedForPeriod}`,
        name: <span className="text-primary">{"Billed for the Period"}</span>,
      },
      {
        link: "",
        name: <span className="text-primary">{capitalize(action || "")}</span>,
      },
    ];
  }, []);

  const breadcrumbData = fromDashboard
    ? breadcrumbDataDashboard
    : breadcrumbDataActivity;

  const breadcrumbTitle = `Billed for the Period - ${communityDetail?.name}`;

  const onBackClickActivity = () => {
    navigate(
      `/${routes.cam}/${routes.activityDashboard}/${routes.billedForPeriod}`
    );
  };

  const onBackClickDashboard = () => {
    navigate(`/${routes.cam}/${routes.dashboard}/${routes.billedForPeriod}`);
  };

  const onBackClick = fromDashboard
    ? onBackClickDashboard
    : onBackClickActivity;

  const { data: documentTypes, isLoading: isDocumentTypeLoading } =
    useGetDocumentTypeDropdown({
      communityId: communityDetail?.communityId as string,
      queryParams: {
        enabled: !!communityDetail?.communityId,
      },
    });

  const { data: glData, isLoading: isLoadingGlMaster } = useGetGlMasterData({
    communityId: communityDetail?.communityId,
    queryParams: {
      enabled: !!communityDetail?.communityId,
    },
  });

  const { data: paymentTermOptions, isLoading: isPaymentTermLoading } =
    useGetPaymentTerms();

  const { data: propertyModels, isLoading: isPropertyModelLoading } =
    useGetPropertyModels();

  const { data: propertyBlocks, isLoading: isPropertyBlockLoading } =
    useGetPropertyBlocks();

  const { data: billingCategory, isLoading: isBillingCategoryLoading } =
    useGetBillingCategoryByCommunity({communityId: communityDetail?.communityId});

  useEffect(() => {
    if (documentTypes) {
      if (!billingId) {
        const documentTypeId = documentTypes
          ?.find((data: any) => {
            return data?.documentType?.toLowerCase() == "ob";
          })
          ?.documentTypeId?.toString();
        if (documentTypeId) {
          form.setValue(DOCUMENT_TYPE, documentTypeId);
        } else {
          toast.error(
            "The Document Type 'OB' was not found. Kindly add it to proceed."
          );
          onBackClick();
        }
      }
      setKeys((prev: any) => ({ ...prev, documentTypes: Math.random() * 4 }));
    }
  }, [documentTypes]);

  useEffect(() => {
    if (paymentTermOptions) {
      setKeys((prev: any) => ({ ...prev, paymentTerm: Math.random() * 4 }));
    }
  }, [paymentTermOptions]);

  const { data: pendingBillingData, isLoading: isLoadingBillingData, refetch: refetchPendingBillingData } =
    useGetPostedDocById({
      communityId: communityDetail?.communityId,
      id: billingId as string,
      entryView: isEntryView,
      queryParams: {
        enabled: !!billingId,
      },
    });

  useEffect(() => {
    if (billingId) {
      refetchPendingBillingData();
    }
  }, [isEntryView]);

  useEffect(() => {
    if (pendingBillingData) {
      form.reset({
        [DOCUMENT_TYPE]:
          pendingBillingData?.data?.header?.[DOCUMENT_TYPE]?.toString() || "",
        [DOCUMENT_ID]:
          pendingBillingData?.data?.header?.postDocumentHeaderId?.toString() ||
          pendingBillingData?.data?.header?.[DOCUMENT_ID]?.toString() ||
          billingId?.toString() ||
          "",
        [BILLING_MONTH_YEAR]:
          `${pendingBillingData?.data?.header?.postingPeriod?.toString()}/${pendingBillingData?.data?.header?.postingYear?.toString()}` ||
          "",
        [BILLING_DATE]: pendingBillingData?.data?.header?.postingDate
          ? moment(pendingBillingData?.data?.header?.postingDate)?.toString()
          : "",
        [BILLING_AMOUNT]:
          pendingBillingData?.data?.header?.approvedAmount?.toString() || "",
        [DUE_DATE]: pendingBillingData?.data?.header?.[DUE_DATE]
          ? moment(pendingBillingData?.data?.header?.[DUE_DATE])
          : "",
        [TRANSACTION_TYPE]:
          pendingBillingData?.data?.header?.[TRANSACTION_TYPE]?.toString() ||
          "",
        [GL_ACCOUNT]:
          pendingBillingData?.data?.header?.pricingResponse?.[
            GL_ACCOUNT
          ]?.toString() || "",
        [PAYMENT_TERM]:
          pendingBillingData?.data?.header?.[PAYMENT_TERM]?.toString() || "",
        [COMMENT]: pendingBillingData?.data?.header?.comments?.toString() || "",
        [POSTING_DATE]: pendingBillingData?.data?.header?.[POSTING_DATE]
          ? moment(pendingBillingData?.data?.header?.[POSTING_DATE])?.toString()
          : "",
        [LINE_ITEMS]: pendingBillingData?.data?.lineItems?.map((item: any) => ({
          ...item,
          [BILLING_CATEGORY]: (item?.billingCategory ?? item?.[BILLING_CATEGORY])?.toString() || "",
          [PROPERTY_MODEL]: (item?.propertyModelName ?? item?.[PROPERTY_MODEL])?.toString() || "",
          [PROPERTY_BLOCK]: item?.[PROPERTY_BLOCK]?.toString() || "",
          [PROPERTY_ID]: item?.[PROPERTY_ID]?.toString() || "",
          [PROPERTY_NAME]: item?.propertyName || "",
          [UNIT_NO]: (item?.unitNo ?? item?.[UNIT_NO])?.toString() || "",
          [STREET_ADDRESS]: (item?.streetAddress ?? item?.[STREET_ADDRESS])?.toString() || "",
          [CURR_OWNER_ID]: item?.[CURR_OWNER_ID]?.toString() || "",
          [CURR_OWNER_LAST_NAME]: (item?.ownershipName ?? item?.[CURR_OWNER_LAST_NAME])?.toString() || "",
          [GL_ACCOUNT]: item?.[GL_ACCOUNT]?.toString(),
          [DRCR]: item?.drCr?.toString() || "",
          [LINE_BILLING_AMOUNT]: item?.[LINE_BILLING_AMOUNT]?.toString() || "",
          [ITEM_TEXT]: item?.[ITEM_TEXT]?.toString() || "",
          [CHECK]: item?.check || false,
        })),
      });

      setKeys({
        ...keys,
        accountName: Math.random() * 2,
        documentTypes: Math.random() * 2,
        paymentTerm: Math.random() * 2,
        billingCategory: Math.random() * 2,
        propertyModel: Math.random() * 2,
        transactionType: Math.random() * 2,
      });
    }
  }, [pendingBillingData]);

  const onChangeFilterCriteria = (key: string, value: string) => {
    setFilterCriteria((prev) => ({ ...prev, [key]: value }));
  };

  const onApplyFilter = () => {
    const lineItems = [...pendingBillingData?.data?.lineItems]
      ?.filter((item: any) => {
        if (
          filterCriteria?.[PROPERTY_NAME] &&
          filterCriteria?.[CURR_OWNER_LAST_NAME]
        ) {
          ("inside first IF");
          return (
            item?.property?.[PROPERTY_NAME]?.toLowerCase()?.includes(
              filterCriteria?.[PROPERTY_NAME]?.toLowerCase()
            ) &&
            item?.propertyOwnership?.[
              CURR_OWNER_LAST_NAME
            ]?.toLowerCase()?.includes(
              filterCriteria?.[CURR_OWNER_LAST_NAME]?.toLowerCase()
            )
          );
        }

        if (filterCriteria?.[PROPERTY_NAME]) {
          return item?.property?.[PROPERTY_NAME]?.toLowerCase()?.includes(
            filterCriteria?.[PROPERTY_NAME]?.toLowerCase()
          );
        }

        if (filterCriteria?.[CURR_OWNER_LAST_NAME]) {
          return item?.propertyOwnership?.[
            CURR_OWNER_LAST_NAME
          ]?.toLowerCase()?.includes(
            filterCriteria?.[CURR_OWNER_LAST_NAME]?.toLowerCase()
          );
        }
        return false;
      })
      .map((item: any) => ({
        [BILLING_CATEGORY]:
          billingCategory?.data?.find(
            (data: any) =>
              data?.propertyModelId?.toString() ==
              item?.property?.modelId?.toString()
          )?.categoryName || "",
        [PROPERTY_MODEL]: item?.property?.[PROPERTY_MODEL]?.toString() || "",
        [PROPERTY_BLOCK]: item?.property?.[PROPERTY_BLOCK]?.toString() || "",
        [PROPERTY_ID]: item?.property?.[PROPERTY_ID]?.toString() || "",
        [PROPERTY_NAME]: item?.property?.[PROPERTY_NAME]?.toString() || "",
        [UNIT_NO]: item?.property?.[UNIT_NO]?.toString() || "",
        [STREET_ADDRESS]:
          item?.property?.address?.[STREET_ADDRESS]?.toString() || "",
        [CURR_OWNER_ID]:
          item?.propertyOwnership?.[CURR_OWNER_ID]?.toString() || "",
        [CURR_OWNER_LAST_NAME]:
          item?.propertyOwnership?.[CURR_OWNER_LAST_NAME]?.toString() || "",
        [DRCR]: item?.drCr?.toString() || "",
        [LINE_BILLING_AMOUNT]: item?.[LINE_BILLING_AMOUNT]?.toString() || "",
        [CHECK]: item?.check || false,
      }));

    form.setValue(LINE_ITEMS, lineItems);
  };

  const onClearFilter = () => {
    setFilterCriteria({
      [CURR_OWNER_LAST_NAME]: "",
      [PROPERTY_NAME]: "",
    });

    form.setValue(
      LINE_ITEMS,
      pendingBillingData?.data?.lineItems?.map((item: any) => ({
        [BILLING_CATEGORY]:
          billingCategory?.data?.find(
            (data: any) =>
              data?.propertyModelId?.toString() ==
              item?.property?.modelId?.toString()
          )?.categoryName || "",
        [PROPERTY_MODEL]: item?.property?.[PROPERTY_MODEL]?.toString() || "",
        [PROPERTY_BLOCK]: item?.property?.[PROPERTY_BLOCK]?.toString() || "",
        [PROPERTY_ID]: item?.property?.[PROPERTY_ID]?.toString() || "",
        [PROPERTY_NAME]: item?.property?.[PROPERTY_NAME]?.toString() || "",
        [UNIT_NO]: item?.property?.[UNIT_NO]?.toString() || "",
        [STREET_ADDRESS]:
          item?.property?.address?.[STREET_ADDRESS]?.toString() || "",
        [CURR_OWNER_ID]:
          item?.propertyOwnership?.[CURR_OWNER_ID]?.toString() || "",
        [CURR_OWNER_LAST_NAME]:
          item?.propertyOwnership?.[CURR_OWNER_LAST_NAME]?.toString() || "",
        [DRCR]: item?.drCr?.toString() || "",
        [LINE_BILLING_AMOUNT]: item?.[LINE_BILLING_AMOUNT]?.toString() || "",
        [CHECK]: item?.check || false,
      })),
      { shouldValidate: true, shouldDirty: true }
    );
  };

  return {
    breadcrumbData,
    onBackClick,
    communityDetail,
    form,
    requiredFields,
    glData,
    isDocumentTypeLoading,
    documentTypes,
    isLoadingGlMaster,
    onCancel: onBackClick,
    breadcrumbTitle,
    keys,
    isPaymentTermLoading,
    paymentTermOptions,
    propertyModels,
    isPropertyModelLoading,
    propertyBlocks,
    isPropertyBlockLoading,
    billingCategory,
    isBillingCategoryLoading,
    isLoadingBillingData,
    onChangeFilterCriteria,
    onApplyFilter,
    onClearFilter,
    filterCriteria,
    isEntryView,
    setIsEntryView
  };
};

export default useAddEdit;
